import { useEffect, useRef, useState } from "react";
import RequestCard from "./RequestCard";
import { formatDateRange } from "src/Pages/TimeOff/TimeOff";
import { Avatar, Popover, fullName, shortName, typeUi } from "./timeOffShared";

// Minimal hover card for users whose Time Off display is NOT enabled — keeps the
// pre-existing plain "name + OFF + dates" look.
function PlainCard({ request, onClose }) {
  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <span className="font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px] text-[#1A2332]">
          {fullName(request.user)}
        </span>
        {onClose && (
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#475569] leading-none text-[18px]">
            ×
          </button>
        )}
      </div>
      <span className="mt-2 inline-block rounded-[16px] bg-[#EEF0F9] px-2 py-0.5 text-center text-[12px] font-OpenSansSemiBold leading-[18px] tracking-[0.12px] text-[#5F6477]">
        OFF
      </span>
      <div className="mt-3 flex items-center gap-2 text-[13px] text-[#475569]">
        <img src="/images/common/calendar.svg" alt="calendar" className="h-4 w-4" />
        <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#1A2332]">
          {formatDateRange(request.startDate, request.endDate)}
        </span>
      </div>
    </div>
  );
}

// Time-off pill for one request. When `rich` (the request owner's Time Off
// display is enabled), the pill is coloured/iconed by type and opens the full
// RequestCard. Otherwise it falls back to the plain grey "Name • OFF" pill.
// `onView(request)` opens the shared detail drawer (from the rich card).
function OffChip({ request, rich = false, onView }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const type = typeUi(request.type);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const inTrigger = ref.current && ref.current.contains(e.target);
      const inPopover = e.target.closest && e.target.closest("[data-timeoff-popover]");
      if (!inTrigger && !inPopover) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <>
      <div
        ref={ref}
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 w-full h-[26px] rounded-full px-2 cursor-pointer ${
          rich ? type.chip : "bg-[#EEF0F9]"
        }`}
        title={fullName(request.user)}
      >
        {rich ? (
          <img src={type.icon} alt={type.label} className="w-3.5 h-3.5 shrink-0" />
        ) : (
          <Avatar user={request.user} className="w-5 h-5 shrink-0" />
        )}
        <span
          className={`truncate font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize ${
            rich ? "" : "text-[#334155]"
          }`}
        >
          {shortName(request.user)}
        </span>
        {!rich && (
          <>
            <span className="text-[#94A3B8]">•</span>
            <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#64748B]">
              OFF
            </span>
          </>
        )}
      </div>
      {open && (
        <Popover anchorRef={ref}>
          {rich ? (
            <RequestCard request={request} onClose={() => setOpen(false)} onView={onView} />
          ) : (
            <PlainCard request={request} onClose={() => setOpen(false)} />
          )}
        </Popover>
      )}
    </>
  );
}

export default OffChip;
